//
// Copyright © 2026 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Class, Doc, Ref } from '@hcengineering/core'
import { type Asset, type IntlString, type Plugin, plugin } from '@hcengineering/platform'
import type { AnyComponent } from '@hcengineering/ui'
import type { Dashboard } from './types'

export * from './types'

/**
 * @public
 */
export const dashboardId = 'dashboard' as Plugin

const dashboard = plugin(dashboardId, {
  class: {
    Dashboard: '' as Ref<Class<Dashboard>>
  },
  icon: {
    Application: '' as Asset,
    Dashboard: '' as Asset,
    GadgetStatus: '' as Asset,
    GadgetAssignee: '' as Asset,
    GadgetTrend: '' as Asset,
    GadgetDueSoon: '' as Asset,
    GadgetCalendar: '' as Asset,
    GadgetFilter: '' as Asset,
    GadgetDocument: '' as Asset,
    GadgetMatrix: '' as Asset
  },
  app: {
    Dashboard: '' as Ref<Doc>
  },
  dashboard: {
    Default: '' as Ref<Dashboard>
  },
  component: {
    DashboardApp: '' as AnyComponent
  },
  string: {
    Application: '' as IntlString,
    ConfigLabel: '' as IntlString,
    ConfigDescription: '' as IntlString,
    Dashboard: '' as IntlString,
    Dashboards: '' as IntlString,
    NoDashboards: '' as IntlString,

    // dashboard management
    CreateDashboard: '' as IntlString,
    DashboardName: '' as IntlString,
    DashboardNamePlaceholder: '' as IntlString,
    DeleteDashboard: '' as IntlString,
    DeleteDashboardConfirm: '' as IntlString,
    RenameDashboard: '' as IntlString,
    MyDashboards: '' as IntlString,
    SharedDashboards: '' as IntlString,
    Personal: '' as IntlString,
    Shared: '' as IntlString,
    Visibility: '' as IntlString,
    DefaultDashboardName: '' as IntlString,

    // header / filters
    AllProjects: '' as IntlString,
    ProjectsSelected: '' as IntlString,
    Period: '' as IntlString,
    PeriodDays: '' as IntlString,
    Edit: '' as IntlString,
    Done: '' as IntlString,

    // gadget frame
    AddGadget: '' as IntlString,
    RemoveGadget: '' as IntlString,
    MoveUp: '' as IntlString,
    MoveDown: '' as IntlString,
    GadgetSize: '' as IntlString,
    SizeSmall: '' as IntlString,
    SizeMedium: '' as IntlString,
    SizeLarge: '' as IntlString,
    NoGadgets: '' as IntlString,
    NoGadgetsHint: '' as IntlString,
    GadgetFailed: '' as IntlString,

    // gadget titles + labels
    StatusDistribution: '' as IntlString,
    StatusDistributionDescription: '' as IntlString,
    AssigneeOpen: '' as IntlString,
    AssigneeOpenDescription: '' as IntlString,
    CreatedVsResolved: '' as IntlString,
    CreatedVsResolvedDescription: '' as IntlString,
    DueSoon: '' as IntlString,
    DueSoonDescription: '' as IntlString,
    IssueCalendar: '' as IntlString,
    IssueCalendarDescription: '' as IntlString,
    SavedFilter: '' as IntlString,
    SavedFilterDescription: '' as IntlString,
    SavedDocFilter: '' as IntlString,
    SavedDocFilterDescription: '' as IntlString,
    TwoDimension: '' as IntlString,
    TwoDimensionDescription: '' as IntlString,

    // shared chart / gadget vocabulary
    NoData: '' as IntlString,
    Other: '' as IntlString,
    OtherPeople: '' as IntlString,
    Total: '' as IntlString,
    Unassigned: '' as IntlString,
    UnknownPerson: '' as IntlString,
    Created: '' as IntlString,
    Resolved: '' as IntlString,
    Backlog: '' as IntlString,
    Overdue: '' as IntlString,
    DueToday: '' as IntlString,
    DueSoonBadge: '' as IntlString,
    DaysOverdue: '' as IntlString,
    ShowAll: '' as IntlString,
    AndMore: '' as IntlString,

    // gadget options
    GroupBy: '' as IntlString,
    GroupByStatus: '' as IntlString,
    GroupByPriority: '' as IntlString,
    GroupByComponent: '' as IntlString,
    GroupByMilestone: '' as IntlString,
    IncludeDone: '' as IntlString,
    StackBy: '' as IntlString,
    StackByNone: '' as IntlString,
    StackByPriority: '' as IntlString,
    StackByStatusCategory: '' as IntlString,
    TopN: '' as IntlString,
    IncludeUnassigned: '' as IntlString,
    Bucket: '' as IntlString,
    BucketAuto: '' as IntlString,
    BucketDay: '' as IntlString,
    BucketWeek: '' as IntlString,
    ShowBacklogLine: '' as IntlString,
    DateField: '' as IntlString,
    DateFieldDueDate: '' as IntlString,
    DateFieldDeadline: '' as IntlString,
    SoonDays: '' as IntlString,
    OnlyMine: '' as IntlString,
    NoComponent: '' as IntlString,
    NoMilestone: '' as IntlString,
    NoPriority: '' as IntlString,
    Today: '' as IntlString,
    PickADay: '' as IntlString,
    TotalIssues: '' as IntlString,
    DateFieldCreatedOn: '' as IntlString,
    DateFieldModifiedOn: '' as IntlString,
    DateFieldStartDate: '' as IntlString,
    PickSavedFilter: '' as IntlString,
    NoSavedFilters: '' as IntlString,
    NoSavedFiltersHint: '' as IntlString,
    NoSavedDocFilters: '' as IntlString,
    NoSavedDocFiltersHint: '' as IntlString,
    PickSavedDocFilter: '' as IntlString,
    TotalDocuments: '' as IntlString,
    SortBy: '' as IntlString,
    SortByModified: '' as IntlString,
    SortByCreated: '' as IntlString,
    SortByTitle: '' as IntlString,
    Limit: '' as IntlString,
    MySavedFilters: '' as IntlString,
    SharedSavedFilters: '' as IntlString,
    ChangeFilter: '' as IntlString,
    FilterUnavailable: '' as IntlString,
    NoFilter: '' as IntlString,
    Filter: '' as IntlString,
    CategoryBacklog: '' as IntlString,
    CategoryTodo: '' as IntlString,
    CategoryInProgress: '' as IntlString,
    CategoryDone: '' as IntlString,
    CategoryCancelled: '' as IntlString,
    Columns: '' as IntlString,
    ResetColumns: '' as IntlString,
    ColumnLimit: '' as IntlString,
    Urgent: '' as IntlString,
    High: '' as IntlString,
    Medium: '' as IntlString,
    Low: '' as IntlString,

    // two dimensional statistics
    RowAxis: '' as IntlString,
    ColumnAxis: '' as IntlString,
    AxisAssignee: '' as IntlString,
    AxisProject: '' as IntlString,
    RowTopN: '' as IntlString,
    ColumnTopN: '' as IntlString,
    Display: '' as IntlString,
    DisplayCount: '' as IntlString,
    DisplayPercent: '' as IntlString,
    CellLimit: '' as IntlString,
    CellLimited: '' as IntlString,
    StatusByCategory: '' as IntlString,
    DepartedExcluded: '' as IntlString
  }
})

export default dashboard
